/** @file Contains the sidebar button to enter and leave student view. */
import { faGlasses } from '@fortawesome/pro-regular-svg-icons/faGlasses';
import { faSlash } from '@fortawesome/pro-solid-svg-icons/faSlash';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { assert, expr } from '@codegrade/utils';
import client from '@/client';
import Icon from '@/components/Icon';
// @ts-expect-error This component is not yet typed
import SubmitButton from '@/components/SubmitButton';
import * as permissions from '@/permissions';
import { CoursesStore, SiteSettingsStore, UserStore } from '@/store';
import * as utils from '@/utils';
export default tsx.component({
    name: 'toggle-student-view-button',
    props: {
        loggedInUser: p.ofType().required,
    },
    /** @returns The data for this component. */
    data() {
        return { disabling: false };
    },
    computed: {
        /** @returns Whether the feature to use student view is enabled. */
        isStudentViewFeatureEnabled() {
            return SiteSettingsStore.getSetting()('STUDENT_VIEW_ENABLED', this.loggedInUser.tenantId);
        },
        /** @returns Whether we are currently in student view. */
        isCurrentlyInStudentView() {
            return this.loggedInUser.getStudentViewState().tag === 'enabled';
        },
        /** @returns The rendered icon for the toggle button. */
        iconNode() {
            return utils.ifExpr(this.isCurrentlyInStudentView, () => (<span class="fa-stack">
                        <Icon class="fa-stack-1x" icon={faGlasses}/>
                        <Icon class="fa-stack-1x" style={{ 'margin-left': '-.5rem' }} scale={1.25} icon={faSlash}/>
                    </span>), () => <Icon icon={faGlasses}/>);
        },
        /** @returns The message to show on the toggle button. */
        popoverMessage() {
            return this.isCurrentlyInStudentView ? 'Leave student view' : 'Enable student view';
        },
        /** @returns The success alert for screen readers on the submitbutton action. */
        successAlert() {
            return this.disabling
                ? 'You have left the student view'
                : 'You have enabled student view';
        },
        /** @returns The enable/disable action for the toggle button. */
        submitButtonAction() {
            if (!this.isStudentViewFeatureEnabled) {
                return Maybe.empty();
            }
            if (this.disabling) {
                return Maybe.of(this.disableStudentView);
            }
            const studentViewState = this.loggedInUser.getStudentViewState();
            return expr.match(studentViewState, 'tag', {
                disabled: () => this.enableStudentViewFunction,
                enabled: ({ canLeave }) => {
                    if (!canLeave) {
                        return Maybe.empty();
                    }
                    return Maybe.of(this.disableStudentView);
                },
            });
        },
        /** @returns The student view page to go to after enabling. */
        studentViewPage() {
            const meta = this.$route.meta;
            const studentView = meta.getStudentViewPage(this.$route).extract();
            if (studentView == null) {
                return Maybe.empty();
            }
            const course = CoursesStore.getCourse()(studentView.courseId).extract();
            if (course == null ||
                !this.loggedInUser.hasCoursePermission(permissions.CoursePermissions.can_edit_course_roles, course)) {
                return Maybe.empty();
            }
            return Maybe.of(studentView);
        },
        /**
         * Get a function to enable student view.
         *
         * We do so by removing the set of permissions for the course that the
         * teacher should not have.
         *
         * Note that we DO NOT restrict the 'can_see_hidden_assignments' permission as we have to
         * support the teacher to inspect their assignment before they are available for the
         * students.
         *
         * @returns Maybe, if we can on the current route, a function to enable student view.
         */
        enableStudentViewFunction() {
            return this.studentViewPage.map(studentView => 
            /** @returns A submit button result. */
            async () => {
                const { cgResult } = await client.p.course.getCourseRoles({
                    courseId: studentView.courseId,
                    withRoles: true,
                });
                for (const role of cgResult) {
                    if (role.name === 'Student') {
                        assert.AssertionError.assert(role instanceof api.CourseRoleAsJSONWithPerms);
                        const permMap = role.perms;
                        // eslint-disable-next-line no-await-in-loop
                        await UserStore.addRestrictions({
                            restrictions: {
                                removedPermissions: {
                                    course: new Set(permissions.allCoursePermissions.filter(perm => perm !== 'can_see_hidden_assignments' &&
                                        !permMap[perm])),
                                },
                                forContext: {
                                    courseId: studentView.courseId,
                                },
                            },
                        });
                        this.$router.push(studentView.location);
                        return {};
                    }
                }
                throw new Error('No role with the name "Student" was found.');
            });
        },
    },
    methods: {
        /** @returns A function to disable student view. */
        async disableStudentView() {
            this.disabling = true;
            return {
                onSuccess: async () => await UserStore.popRestrictions(),
                onAfterSuccess: async () => {
                    this.disabling = false;
                },
            };
        },
    },
    /** @returns The rendered component. */
    render() {
        return utils.ifJustOrEmpty(this.submitButtonAction, submit => (<div>
                <div class="sidebar-bottom" v-b-popover_top_hover_focus={this.popoverMessage}>
                    <SubmitButton submit={submit} class="toggle-student-view-btn sidebar-bottom-item d-inline border-0 rounded-0 shadow-none w-100 bg-transparent" style={{ padding: this.isCurrentlyInStudentView ? '0.125rem' : '' }} aria-label={this.popoverMessage} successAlert={this.successAlert} onFinally={() => {
                this.disabling = false;
            }}>
                        {this.iconNode}
                    </SubmitButton>
                    <hr class="separator"/>
                </div>
            </div>));
    },
});
